import { Link } from "react-router-dom";
import type { Article } from "@/types/content";
import { formatViews, getCategoryName } from "@/utils/content";

interface Props {
  article: Article;
  variant?: "default" | "wide" | "compact";
}

export default function ArticleCard({ article, variant = "default" }: Props) {
  if (variant === "compact") {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group flex items-start gap-4 border-b border-background-200 py-4 cursor-pointer last:border-b-0"
      >
        <div className="h-16 w-24 shrink-0 overflow-hidden rounded-md bg-background-200">
          <img
            src={article.image}
            alt={article.title}
            title={`${article.title} - 热点整理`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="min-w-0">
          <h3 className="line-clamp-2 font-heading text-sm font-bold leading-snug text-foreground-900 transition-colors group-hover:text-primary-600">
            {article.title}
          </h3>
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground-500">
            <span className="text-secondary-700">{getCategoryName(article.categoryId)}</span>
            <span>{article.date}</span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {formatViews(article.views)}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === "wide") {
    return (
      <article className="group grid grid-cols-1 gap-5 border-b border-background-200 py-6 sm:grid-cols-[280px_1fr]">
        <Link to={`/article/${article.id}`} className="block cursor-pointer">
          <div className="aspect-[3/2] w-full overflow-hidden rounded-lg bg-background-200">
            <img
              src={article.image}
              alt={article.title}
              title={`${article.title} - 热点整理`}
              className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
            />
          </div>
        </Link>
        <div className="flex flex-col">
          <div className="flex items-center gap-2">
            <span className="rounded-full bg-secondary-100 px-2.5 py-1 text-xs font-medium text-secondary-800">
              {getCategoryName(article.categoryId)}
            </span>
            <span className="text-xs text-foreground-500">{article.readTime} 分钟阅读</span>
          </div>
          <h3 className="mt-3 font-heading text-lg font-bold leading-snug text-foreground-950">
            <Link to={`/article/${article.id}`} className="transition-colors group-hover:text-primary-600 cursor-pointer">
              {article.title}
            </Link>
          </h3>
          <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">{article.summary}</p>
          <div className="mt-auto flex flex-wrap items-center gap-x-4 gap-y-1 pt-4 text-xs text-foreground-500">
            <span>{article.author}</span>
            <span>{article.date}</span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {formatViews(article.views)}
            </span>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50">
      <Link to={`/article/${article.id}`} className="block cursor-pointer">
        <div className="aspect-[16/10] w-full overflow-hidden bg-background-200">
          <img
            src={article.image}
            alt={article.title}
            title={`${article.title} - 热点整理`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </div>
      </Link>
      <div className="flex flex-1 flex-col p-4 md:p-5">
        <div className="flex items-center gap-2">
          <span className="rounded-full bg-accent-100 px-2.5 py-1 text-xs font-medium text-accent-900">
            {getCategoryName(article.categoryId)}
          </span>
          <span className="text-xs text-foreground-500">{article.readTime} 分钟阅读</span>
        </div>
        <h3 className="mt-3 line-clamp-2 font-heading text-base font-bold leading-snug text-foreground-950">
          <Link to={`/article/${article.id}`} className="transition-colors group-hover:text-primary-600 cursor-pointer">
            {article.title}
          </Link>
        </h3>
        <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">{article.summary}</p>
        <div className="mt-4 flex items-center justify-between border-t border-background-200 pt-3 text-xs text-foreground-500">
          <span>{article.date}</span>
          <span className="flex items-center gap-1">
            <i className="ri-eye-line"></i>
            {formatViews(article.views)}
          </span>
        </div>
      </div>
    </article>
  );
}